import {
  HookFormCheckbox,
  HookFormDateInput,
  HookFormDatePicker,
  HookFormEmailInput,
  HookFormMultiSelect,
  HookFormNumberInput,
  HookFormPhoneInput,
  HookFormRadioBoxInput,
  HookFormSelect,
  HookFormTextInput,
  HookFormToggleInput,
} from '@realtyka/real-ui';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { SECTION_ALIAS } from '../../../../constants/OneRealMortgageConstants';
import { FieldObject, FieldObjectTypeEnum } from '../../../../openapi/atlantis';
import {
  BasicAgreementDefinitionCreationCommandAgreementTypeEnum,
  StyleTemplateEnum,
} from '../../../../openapi/yenta';
import { useAppSelector } from '../../../../slices/store';
import { DisplayTypeInputEnum } from '../../../../utils/ConfigDrivenFormUtils';
import { getTextMaskRegexFromDisplayType } from '../../../../utils/MaskUtils';
import { mapLongOptionLabelsToDescription } from '../../../../utils/OnboardingHelpers';
import {
  EMAIL_VALIDATIONS,
  PHONE_NUMBER_VALIDATIONS,
  getDatePickerConfig,
  getInputValidations,
} from '../../../../utils/Validations';
import ReviewApplication from '../../../ReviewApplication/ReviewApplication';
import ControlledBinaryInput from '../../../inputs/ControlledBinaryInput';
import ControlledConsentCheckboxInput from '../../../inputs/ControlledConsentCheckboxInput';
import ControlledCurrencyInput from '../../../inputs/ControlledCurrencyInput';
import ControlledFileInput from '../../../inputs/ControlledFileInput';
import ControlledMaskTextInput from '../../../inputs/ControlledMaskTextInput';
import ControlledMultiSelectInput from '../../../inputs/ControlledMultiSelectInput';
import ControlledSingleSelectDropDownInput from '../../../inputs/ControlledSingleSelectDropDownInput';
import ControlledSingleSelectImageInput from '../../../inputs/ControlledSingleSelectImageInput';
import ControlledSingleSelectInput from '../../../inputs/ControlledSingleSelectInput';
import ControlledToggleInput from '../../../inputs/ControlledToggleInput';
import ControlledUrlRequestInput from '../../../inputs/ControlledUrlRequestInput';
import ControlledSingleSelectCardInputV2 from '../../../inputs/V2/ControlledSingleSelectCardInputV2';
import ApplicationFee from '../../../onboarding/ApplicationFee';
import AddressInput from '../../Misc/AddressInput';
import TeamCompensationField from '../../Misc/TeamCompensationField';
import AgentSignatureComponent from './AgentSignatureComponent';
import RealEstateLicenseUpload from './RealEstateLicenseUpload';
import SignatureComponentV2 from './SignatureComponentV2';
import SingleFileInputCard from './SingleFileInputCard';
import SingleFileInputCardV2 from './SingleFileInputCardV2';
import TeamAddendumSignatureWrapper from './TeamAddendumSignatureWrapper';

interface ConfigDrivenFormInputV2Props {
  name: string;
  field: FieldObject;
  questionAlias?: string;
  onSubmit: (formData: any) => void;
  isSubmitting: boolean;
  checklistItemId: string;
  onNavigateToQuestion?: (questionId: string) => Promise<void>;
}

const ConfigDrivenFormInputV2: React.FC<ConfigDrivenFormInputV2Props> = ({
  name,
  field,
  questionAlias,
  onSubmit,
  checklistItemId,
  onNavigateToQuestion,
}) => {
  const {
    loan: { borrowerDetail },
    application: { currentQuestion },
  } = useAppSelector((state) => state);
  const { control, handleSubmit, clearErrors } = useFormContext();
  const template = currentQuestion?.content?.style?.template;
  const fieldType = field.type;
  const displayType = field.fieldStyle?.displayType;
  // @ts-expect-error - Field type is not defined in the field object
  const readOnly = field.readOnly;
  const handleDocumentSigned = () => {
    clearErrors();
    handleSubmit(onSubmit)();
  };
  if (questionAlias === SECTION_ALIAS.REVIEW_APPLICATION) {
    return (
      <ReviewApplication
        title={field?.label}
        onNavigateToQuestion={onNavigateToQuestion}
      />
    );
  }

  if (displayType === 'HIDDEN') {
    return null;
  }

  if (fieldType === FieldObjectTypeEnum.Binary) {
    return (
      <ControlledBinaryInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        options={[field?.trueOption, field?.falseOption]}
        rules={getInputValidations(field)}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Text) {
    const mask = getTextMaskRegexFromDisplayType(displayType!);

    if (mask) {
      return (
        <ControlledMaskTextInput
          name={name}
          mask={mask}
          control={control}
          label={field.label}
          //@ts-ignore
          placeholder={field.placeholder}
          rules={getInputValidations(field)}
        />
      );
    }

    return (
      <HookFormTextInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        placeholder={field.placeholder}
        rules={getInputValidations(field)}
        disabled={readOnly}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Email) {
    return (
      <HookFormEmailInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        placeholder={field.placeholder}
        type='email'
        rules={{
          ...EMAIL_VALIDATIONS,
          ...getInputValidations(field),
        }}
        disabled={readOnly}
      />
    );
  }

  if (fieldType === 'NUMBER') {
    return (
      <HookFormNumberInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        placeholder={field.placeholder}
        type='number'
        rules={getInputValidations(field)}
        disabled={readOnly}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Phone) {
    return (
      <HookFormPhoneInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        placeholder={field.placeholder}
        rules={{ ...PHONE_NUMBER_VALIDATIONS, ...getInputValidations(field) }}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Money) {
    return (
      <ControlledCurrencyInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        placeholder={field.placeholder}
        rules={getInputValidations(field)}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.SingleSelect) {
    if (displayType === DisplayTypeInputEnum.SINGLE_SELECT_IMAGE) {
      return (
        <ControlledSingleSelectImageInput
          name={name}
          control={control}
          label={field.label}
          //@ts-ignore
          options={field.options}
          rules={getInputValidations(field)}
        />
      );
    }

    if (displayType === DisplayTypeInputEnum.SINGLE_SELECT_DROP_DOWN) {
      if (template === StyleTemplateEnum.RezenAgent1) {
        return (
          <HookFormSelect
            name={name}
            control={control}
            label={field.label}
            //@ts-ignore
            placeholder={field.placeholder}
            //@ts-ignore
            data={field.options}
            rules={getInputValidations(field)}
            searchable
            clearable
          />
        );
      }

      return (
        <ControlledSingleSelectDropDownInput
          name={name}
          control={control}
          label={field.label}
          //@ts-ignore
          placeholder={field.placeholder}
          //@ts-ignore
          options={field.options}
          rules={getInputValidations(field)}
        />
      );
    }

    if (displayType === DisplayTypeInputEnum.SINGLE_SELECT_IMAGE_DESCRIPTION) {
      return (
        <ControlledSingleSelectCardInputV2
          name={name}
          control={control}
          //@ts-ignore
          options={field.options}
          rules={getInputValidations(field)}
        />
      );
    }

    if (displayType === DisplayTypeInputEnum.SINGLE_SELECT_BUTTONS) {
      //@ts-ignore
      const mappedOptions = mapLongOptionLabelsToDescription(field.options, 30);
      const longContent = mappedOptions?.some(
        (option: any) => option.description,
      );
      return (
        <HookFormRadioBoxInput
          name={name}
          control={control}
          label={field.label}
          //@ts-ignore
          options={mappedOptions}
          rules={getInputValidations(field)}
          optionsAlignment={longContent ? 'vertical' : 'horizontal'}
          disabled={readOnly}
        />
      );
    }

    return (
      <ControlledSingleSelectInput
        name={name}
        label={field.label}
        control={control}
        //@ts-ignore
        options={field.options}
        rules={getInputValidations(field)}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.MultiSelect) {
    if (displayType === 'dropdown') {
      return (
        <HookFormMultiSelect
          name={name}
          label={field.label}
          control={control}
          // @ts-ignore
          data={field.options}
          rules={getInputValidations(field)}
          searchable
        />
      );
    }

    return (
      <ControlledMultiSelectInput
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        options={field.options}
        rules={getInputValidations(field)}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Date) {
    const { minDate, maxDate } = getDatePickerConfig(field);

    if (template === StyleTemplateEnum.RezenAgent1) {
      return (
        <HookFormDateInput
          name={name}
          control={control}
          label={field.label}
          //@ts-ignore
          placeholder={field.placeholder}
          minDate={minDate}
          maxDate={maxDate}
          rules={getInputValidations(field)}
          disabled={readOnly}
        />
      );
    }

    return (
      <HookFormDatePicker
        name={name}
        control={control}
        label={field.label}
        //@ts-ignore
        placeholder={field.placeholder}
        minDate={minDate}
        maxDate={maxDate}
        rules={getInputValidations(field)}
        disabled={readOnly}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Toggle) {
    if (displayType === DisplayTypeInputEnum.CONSENT_CHECKBOX) {
      return (
        <ControlledConsentCheckboxInput
          name={name}
          label={field.label}
          control={control}
          //@ts-ignore
          trueOption={field?.trueOption}
          //@ts-ignore
          falseOption={field?.falseOption}
          //@ts-ignore
          defaultValue={field?.presetAnswer?.toString()}
          rules={getInputValidations(field)}
        />
      );
    }

    if (displayType === 'CHECKBOX') {
      return (
        <HookFormCheckbox
          name={name}
          size='sm'
          control={control}
          label={field.label}
          rules={getInputValidations(field)}
          disabled={readOnly}
        />
      );
    }

    if (template === StyleTemplateEnum.RezenAgent1) {
      return (
        <HookFormToggleInput
          name={name}
          control={control}
          label={field.label}
          rules={getInputValidations(field)}
          labelPosition='right'
          disabled={readOnly}
        />
      );
    }

    return (
      <ControlledToggleInput
        name={name}
        label={field.label}
        control={control}
        //@ts-ignore
        trueOption={field?.trueOption}
        //@ts-ignore
        falseOption={field?.falseOption}
        //@ts-ignore
        defaultValue={field?.presetAnswer?.toString()}
        rules={getInputValidations(field)}
      />
    );
  }

  if (fieldType === FieldObjectTypeEnum.Address) {
    return <AddressInput name={name} field={field} />;
  }

  if (field.type === FieldObjectTypeEnum.FileRequest) {
    if (displayType === DisplayTypeInputEnum.SINGLE_FILE) {
      if (template === StyleTemplateEnum.RezenAgent1) {
        if (questionAlias?.includes(SECTION_ALIAS.REAL_ESTATE_LICENSE)) {
          return (
            <RealEstateLicenseUpload
              name={name}
              field={field}
              checklistItemId={checklistItemId}
            />
          );
        }

        return (
          <SingleFileInputCardV2
            name={name}
            field={field}
            checklistItemId={checklistItemId}
          />
        );
      }

      return (
        <SingleFileInputCard
          name={name}
          field={field}
          checklistItemId={checklistItemId}
        />
      );
    }

    return (
      <ControlledFileInput
        name={name}
        label={field.label}
        control={control}
        checklistItemId={checklistItemId}
        //@ts-ignore
        sendTo={borrowerDetail?.dropboxEmailAddress}
        //@ts-ignore
        options={[field?.trueOption, field?.falseOption]}
        rules={getInputValidations(field)}
      />
    );
  }

  if (field.type === FieldObjectTypeEnum.UrlRequest) {
    return (
      <ControlledUrlRequestInput
        name={name}
        control={control}
        label={field.label!}
        //@ts-ignore
        urlSourcePath={field.urlSourcePath!}
      />
    );
  }

  if (field.type === FieldObjectTypeEnum.SignatureRequest) {
    const teamAddendum =
      // @ts-expect-error - Field type is not defined in the field object
      field.agreementType ===
      BasicAgreementDefinitionCreationCommandAgreementTypeEnum.TeamAddendumAgreement;

    if (teamAddendum) {
      return (
        <TeamAddendumSignatureWrapper
          name={name}
          field={field}
          documentSignedCallback={handleDocumentSigned}
          browserMode={false}
        />
      );
    }

    if (template === StyleTemplateEnum.RezenAgent1) {
      return (
        <AgentSignatureComponent
          name={name}
          field={field}
          documentSignedCallback={handleSubmit(onSubmit)}
        />
      );
    }

    return (
      <SignatureComponentV2
        name={name}
        field={field}
        documentSignedCallback={(_isSignedAlready) => handleSubmit(onSubmit)()}
      />
    );
  }

  // @ts-ignore
  if (field.type === 'PAYMENT_REQUEST') {
    return <ApplicationFee field={field} onSubmit={handleSubmit(onSubmit)} />;
  }

  // @ts-expect-error - Field type is not defined in the field object
  if (fieldType === 'TEAM_COMPENSATION_PLAN_ATTESTATION') {
    return <TeamCompensationField name={name} />;
  }

  return undefined;
};

export default ConfigDrivenFormInputV2;
